import { useQuery } from '@tanstack/react-query';

import { Input } from '@/components/ui/input';
import { apiClient } from '@/lib/api/axios';

import { VariableAutocomplete } from '../VariableAutocomplete';

import type { HandoffNodeData } from '@repo/shared-types';

function useConfigurations() {
  return useQuery({
    queryKey: ['configurations'],
    queryFn: async () => {
      const res = await apiClient.get<{ id: string; name: string }[]>('/configurations');
      return res.data;
    },
  });
}

interface HandoffConfigProps {
  data: HandoffNodeData;
  onChange: (data: HandoffNodeData) => void;
}

export const HandoffConfig = ({ data, onChange }: HandoffConfigProps) => {
  const { data: configurations } = useConfigurations();

  const update = (fields: Partial<HandoffNodeData>) => {
    onChange({ ...data, ...fields });
  };

  return (
    <div className="space-y-4">
      <p className="text-[10px] text-gray-500">
        For webhook workflows backing an agent&apos;s hand-off custom tool. Ends the current AI chat
        session (its post-chat workflow still runs), pins the contact to the target configuration,
        and the target agent replies to the customer&apos;s last message in the same WhatsApp
        thread.
      </p>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Label</label>
        <Input
          value={data.label || ''}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="AI Handoff"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Target Configuration</label>
        <select
          value={data.configurationId || ''}
          onChange={(e) => update({ configurationId: e.target.value })}
          className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
        >
          <option value="">Select configuration...</option>
          {configurations?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[10px] text-gray-500">
          The configuration whose agent takes over the conversation.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Handoff summary</label>
        <VariableAutocomplete
          value={data.summary || ''}
          onChange={(v) => update({ summary: v })}
          placeholder="Customer wants to browse other cars — was in the visit-confirmation flow"
          multiline
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Context handed to the new agent — why the conversation was handed over. Supports{' '}
          {'{{template}}'} variables.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Pin duration (hours)</label>
        <Input
          type="number"
          min={1}
          max={720}
          value={data.expiresHours ?? 48}
          onChange={(e) => update({ expiresHours: Number(e.target.value) })}
          placeholder="48"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          How long the contact stays routed to the target configuration.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">
          Call ID (advanced, optional)
        </label>
        <VariableAutocomplete
          value={data.callId || ''}
          onChange={(v) => update({ callId: v })}
          placeholder="{{webhook.body.callId}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Defaults to the webhook body&apos;s callId — leave empty in tool-backed webhook workflows.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Output Key</label>
        <Input
          value={data.outputKey || 'handoff'}
          onChange={(e) => update({ outputKey: e.target.value })}
          placeholder="handoff"
        />
      </div>
    </div>
  );
};
